/**
 * Runtime Tracing: Execution Tracing System
 *
 * Provides execution tracing capabilities for debugging,
 * performance monitoring, and operational visibility.
 *
 * @platform-infrastructure
 */

import { RuntimeTrace, RuntimeTraceEvent, RuntimeExecutionId, CorrelationId } from '../contracts'

/**
 * Runtime Tracer
 *
 * Manages execution traces and provides tracing capabilities.
 */
export class RuntimeTracer {
  private static traces: Map<RuntimeExecutionId, RuntimeTrace> = new Map()
  private static maxTraces: number = 1000 // Limit memory usage

  /**
   * Start a new trace
   */
  static startTrace(
    executionId: RuntimeExecutionId,
    correlationId: CorrelationId,
    operation: string
  ): RuntimeTrace {
    const trace: RuntimeTrace = {
      executionId,
      correlationId,
      events: [],
      startTime: new Date(),
    }

    // Add initial event
    this.addEvent(trace, 'start', `Started: ${operation}`)

    // Store trace
    this.traces.set(executionId, trace)

    // Cleanup old traces if needed
    this.cleanupOldTraces()

    return trace
  }

  /**
   * End a trace
   */
  static endTrace(executionId: RuntimeExecutionId, result?: 'success' | 'failure'): void {
    const trace = this.traces.get(executionId)
    if (!trace) return

    trace.endTime = new Date()
    trace.duration = trace.endTime.getTime() - trace.startTime.getTime()

    // Add completion event
    this.addEvent(trace, 'complete', `Completed with ${result || 'unknown result'}`, {
      result,
      duration: trace.duration,
    })
  }

  /**
   * Add an event to a trace
   */
  static addEvent(
    trace: RuntimeTrace,
    type: RuntimeTraceEvent['type'],
    message: string,
    data?: Record<string, any>,
    duration?: number
  ): void {
    const event: RuntimeTraceEvent = {
      timestamp: new Date(),
      type,
      message,
      data,
      duration,
    }

    trace.events.push(event)
  }

  /**
   * Add progress event to current trace
   */
  static traceProgress(
    executionId: RuntimeExecutionId,
    message: string,
    data?: Record<string, any>
  ): void {
    const trace = this.traces.get(executionId)
    if (!trace) return

    this.addEvent(trace, 'progress', message, data)
  }

  /**
   * Add error event to current trace
   */
  static traceError(
    executionId: RuntimeExecutionId,
    error: Error,
    context?: Record<string, any>
  ): void {
    const trace = this.traces.get(executionId)
    if (!trace) return

    this.addEvent(trace, 'error', `Error: ${error.message}`, {
      error: error.message,
      stack: error.stack,
      ...context,
    })
  }

  /**
   * Add retry event to current trace
   */
  static traceRetry(
    executionId: RuntimeExecutionId,
    attempt: number,
    maxAttempts: number,
    delay: number
  ): void {
    const trace = this.traces.get(executionId)
    if (!trace) return

    this.addEvent(trace, 'retry', `Retry attempt ${attempt}/${maxAttempts}`, {
      attempt,
      maxAttempts,
      delay,
    })
  }

  /**
   * Add timeout event to current trace
   */
  static traceTimeout(
    executionId: RuntimeExecutionId,
    timeoutMs: number
  ): void {
    const trace = this.traces.get(executionId)
    if (!trace) return

    this.addEvent(trace, 'timeout', `Operation timed out after ${timeoutMs}ms`, {
      timeoutMs,
    })
  }

  /**
   * Get a trace by execution ID
   */
  static getTrace(executionId: RuntimeExecutionId): RuntimeTrace | undefined {
    return this.traces.get(executionId)
  }

  /**
   * Get all traces for a correlation ID
   */
  static getTracesByCorrelationId(correlationId: CorrelationId): RuntimeTrace[] {
    return Array.from(this.traces.values()).filter(
      trace => trace.correlationId === correlationId
    )
  }

  /**
   * Get all active traces (not completed)
   */
  static getActiveTraces(): RuntimeTrace[] {
    return Array.from(this.traces.values()).filter(
      trace => !trace.endTime
    )
  }

  /**
   * Get recent traces (completed within last N minutes)
   */
  static getRecentTraces(minutes: number = 5): RuntimeTrace[] {
    const cutoff = Date.now() - (minutes * 60 * 1000)

    return Array.from(this.traces.values()).filter(
      trace => trace.endTime && trace.endTime.getTime() > cutoff
    )
  }

  /**
   * Export trace for debugging
   */
  static exportTrace(executionId: RuntimeExecutionId): string | null {
    const trace = this.getTrace(executionId)
    if (!trace) return null

    return JSON.stringify({
      executionId: trace.executionId,
      correlationId: trace.correlationId,
      startTime: trace.startTime.toISOString(),
      endTime: trace.endTime?.toISOString(),
      duration: trace.duration,
      eventCount: trace.events.length,
      events: trace.events.map(event => ({
        timestamp: event.timestamp.toISOString(),
        type: event.type,
        message: event.message,
        data: event.data,
        duration: event.duration,
      })),
    }, null, 2)
  }

  /**
   * Clear old traces to prevent memory leaks
   */
  private static cleanupOldTraces(): void {
    if (this.traces.size <= this.maxTraces) return

    // Remove oldest completed traces
    const completedTraces = Array.from(this.traces.entries())
      .filter(([, trace]) => trace.endTime)
      .sort(([, a], [, b]) => (a.endTime?.getTime() || 0) - (b.endTime?.getTime() || 0))

    const tracesToRemove = completedTraces.slice(0, this.traces.size - this.maxTraces + 100)

    tracesToRemove.forEach(([executionId]) => {
      this.traces.delete(executionId)
    })
  }

  /**
   * Set maximum number of traces to keep
   */
  static setMaxTraces(max: number): void {
    this.maxTraces = max
    this.cleanupOldTraces()
  }

  /**
   * Clear all traces
   */
  static clearAllTraces(): void {
    this.traces.clear()
  }
}